import { useEffect, useState } from 'react';
import type { PartnerCommissionListItem, PartnerCommissionStatus, PartnerListItem } from '@accestra/types';
import {
  Button,
  C,
  Card,
  Field,
  Pill,
  TableWrap,
  asNullableText,
  friendlyError,
  formatMaybeDate,
  formatMaybeDateTime,
  formatMoney,
  inputStyle,
  paFetch,
  statusColor,
  tdS,
  textareaStyle,
  thS,
  titleCase,
} from './shared';

const COMMISSION_STATUS_OPTIONS: Array<PartnerCommissionStatus | 'all'> = [
  'all',
  'pending',
  'approved',
  'paid',
  'voided',
];

interface PartnerListResponse {
  partners: PartnerListItem[];
  total: number;
}

interface CommissionListResponse {
  commissions: PartnerCommissionListItem[];
  total: number;
}

export default function CommissionsPanel() {
  const [partners, setPartners] = useState<PartnerListItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<PartnerCommissionStatus | 'all'>('all');
  const [partnerFilter, setPartnerFilter] = useState<string>('all');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [commissions, setCommissions] = useState<PartnerCommissionListItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [payoutPartnerId, setPayoutPartnerId] = useState<string>('all');
  const [payoutNotes, setPayoutNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selectedCommissions = commissions.filter((commission) => selectedIds.includes(commission.id));
  const selectedPartnerIds = Array.from(new Set(selectedCommissions.map((commission) => commission.partnerId)));

  async function loadPartners() {
    try {
      const result = await paFetch<PartnerListResponse>('GET', '/platform-admin/partners?page=1&pageSize=100');
      setPartners(result.partners);
      if (payoutPartnerId === 'all' && result.partners[0]) {
        setPayoutPartnerId(result.partners[0]!.id);
      }
    } catch (nextError) {
      setError(friendlyError(nextError));
    }
  }

  async function loadCommissions() {
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams({
        page: '1',
        pageSize: '100',
      });
      if (statusFilter !== 'all') {
        query.set('status', statusFilter);
      }
      if (partnerFilter !== 'all') {
        query.set('partnerId', partnerFilter);
      }
      if (createdFrom) {
        query.set('createdFrom', new Date(`${createdFrom}T00:00:00`).toISOString());
      }
      if (createdTo) {
        query.set('createdTo', new Date(`${createdTo}T23:59:59`).toISOString());
      }

      const result = await paFetch<CommissionListResponse>('GET', `/platform-admin/partner-commissions?${query.toString()}`);
      setCommissions(result.commissions);
      setSelectedIds((current) => current.filter((id) => result.commissions.some((commission) => commission.id === id)));
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadPartners();
  }, []);

  useEffect(() => {
    void loadCommissions();
  }, [statusFilter, partnerFilter, createdFrom, createdTo]);

  function toggleSelection(id: string) {
    setSelectedIds((current) => (
      current.includes(id)
        ? current.filter((currentId) => currentId !== id)
        : [...current, id]
    ));
  }

  async function runCommissionAction(commissionId: string, action: 'approve' | 'void') {
    setBusyAction(`${action}:${commissionId}`);
    setFeedback(null);
    setError(null);
    try {
      await paFetch('POST', `/platform-admin/partner-commissions/${commissionId}/${action}`, {});
      await loadCommissions();
      setFeedback(`Commission ${action}ed.`);
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setBusyAction(null);
    }
  }

  async function createPayout(useSelectedRows: boolean) {
    if (payoutPartnerId === 'all') {
      setError('Choose a partner for payout creation.');
      return;
    }

    setBusyAction(useSelectedRows ? 'selected-payout' : 'all-payout');
    setFeedback(null);
    setError(null);
    try {
      await paFetch('POST', '/platform-admin/partner-payouts', {
        partnerId: payoutPartnerId,
        commissionIds: useSelectedRows ? selectedIds : undefined,
        notes: asNullableText(payoutNotes),
      });
      await loadCommissions();
      setSelectedIds([]);
      setPayoutNotes('');
      setFeedback(useSelectedRows ? 'Payout draft created from selected rows.' : 'Payout draft created from all eligible rows for the selected partner.');
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setBusyAction(null);
    }
  }

  const canCreateSelectedPayout = (
    selectedIds.length > 0
    && selectedPartnerIds.length === 1
    && selectedPartnerIds[0] === payoutPartnerId
  );

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <Card
        title="Commission controls"
        subtitle="Filter ledger rows, approve or void individual commissions, and create founder-managed payout drafts."
      >
        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '10px 12px', color: C.danger, fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}
        {feedback && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '10px 12px', color: C.success, fontSize: 13, marginBottom: 12 }}>
            {feedback}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, marginBottom: 16 }}>
          <Field label="Status">
            <select style={inputStyle} value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as PartnerCommissionStatus | 'all')}>
              {COMMISSION_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status === 'all' ? 'All statuses' : titleCase(status)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Partner">
            <select style={inputStyle} value={partnerFilter} onChange={(event) => setPartnerFilter(event.target.value)}>
              <option value="all">All partners</option>
              {partners.map((partner) => <option key={partner.id} value={partner.id}>{partner.name}</option>)}
            </select>
          </Field>
          <Field label="Created from">
            <input type="date" style={inputStyle} value={createdFrom} onChange={(event) => setCreatedFrom(event.target.value)} />
          </Field>
          <Field label="Created to">
            <input type="date" style={inputStyle} value={createdTo} onChange={(event) => setCreatedTo(event.target.value)} />
          </Field>
        </div>

        {loading ? (
          <div style={{ color: C.muted, fontSize: 13 }}>Loading commissions…</div>
        ) : (
          <TableWrap>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thS}>Select</th>
                  <th style={thS}>Partner</th>
                  <th style={thS}>Organisation</th>
                  <th style={thS}>Amount</th>
                  <th style={thS}>Status</th>
                  <th style={thS}>Period</th>
                  <th style={thS}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {commissions.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ ...tdS, textAlign: 'center', color: C.muted }}>No commissions match the current filters.</td>
                  </tr>
                ) : commissions.map((commission) => {
                  const selectable = commission.commissionStatus === 'approved' && !commission.payoutId;
                  return (
                    <tr key={commission.id}>
                      <td style={tdS}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(commission.id)}
                          disabled={!selectable}
                          onChange={() => toggleSelection(commission.id)}
                        />
                      </td>
                      <td style={tdS}>{commission.partnerName}</td>
                      <td style={tdS}>
                        <div>{commission.organisationName || 'Manual attribution'}</div>
                        <div style={{ color: C.muted, fontSize: 12 }}>{commission.sourceInvoiceId || 'No invoice id'}</div>
                      </td>
                      <td style={tdS}>{formatMoney(commission.commissionAmount, commission.currency)}</td>
                      <td style={tdS}><Pill label={titleCase(commission.commissionStatus)} tone={statusColor(commission.commissionStatus)} /></td>
                      <td style={tdS}>
                        {formatMaybeDate(commission.billingPeriodStart)} to {formatMaybeDate(commission.billingPeriodEnd)}
                      </td>
                      <td style={tdS}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {commission.commissionStatus !== 'paid' && (
                            <Button
                              disabled={busyAction !== null}
                              onClick={() => void runCommissionAction(commission.id, 'approve')}
                            >
                              {busyAction === `approve:${commission.id}` ? 'Approving…' : 'Approve'}
                            </Button>
                          )}
                          {commission.commissionStatus !== 'paid' && commission.commissionStatus !== 'voided' && (
                            <Button
                              variant="ghost"
                              disabled={busyAction !== null}
                              onClick={() => void runCommissionAction(commission.id, 'void')}
                            >
                              {busyAction === `void:${commission.id}` ? 'Voiding…' : 'Void'}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>

      <Card
        title="Create payout draft"
        subtitle="Create a payout from selected commission rows or all eligible approved rows for one partner."
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
          <Field label="Partner">
            <select style={inputStyle} value={payoutPartnerId} onChange={(event) => setPayoutPartnerId(event.target.value)}>
              <option value="all">Select a partner</option>
              {partners.map((partner) => <option key={partner.id} value={partner.id}>{partner.name}</option>)}
            </select>
          </Field>
          <Field label="Selected rows">
            <div style={{ minHeight: 42, display: 'flex', alignItems: 'center', color: C.text, fontSize: 13 }}>
              {selectedIds.length} selected
            </div>
          </Field>
        </div>

        <div style={{ marginTop: 12 }}>
          <Field label="Payout notes">
            <textarea style={textareaStyle} value={payoutNotes} onChange={(event) => setPayoutNotes(event.target.value)} />
          </Field>
        </div>

        <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            variant="primary"
            disabled={busyAction !== null || !canCreateSelectedPayout}
            onClick={() => void createPayout(true)}
          >
            {busyAction === 'selected-payout' ? 'Creating…' : 'Create from selected rows'}
          </Button>
          <Button
            disabled={busyAction !== null || payoutPartnerId === 'all'}
            onClick={() => void createPayout(false)}
          >
            {busyAction === 'all-payout' ? 'Creating…' : 'Create from all eligible rows'}
          </Button>
        </div>

        <div style={{ marginTop: 12, color: C.muted, fontSize: 12, lineHeight: 1.6 }}>
          Selected payout rows must belong to the same partner and already be in the approved state with no payout attached.
        </div>
      </Card>
    </div>
  );
}
